// src/v2/components/ToolbarViewToggle.jsx
//
// VIEW-TOGGLE-HEIGHT-UNIFY — shared segmented control for the view-mode
// pill (List/Tiles/Map on Offices, List/Timeline on Leases,
// Plan/What-If/Timeline on Market Plan).
//
// Before: three separate hand-rolled implementations —
//   Offices   h=26  (inline <button>, padding "6px 12px")
//   Leases    h=24  (<ToggleButton>, padding "5px 14px")
//   Plan      not a <button> at all (<span role="button">) — missed by queries
// After: all three use this component; height pinned to T.toolbarSegH (26)
// so a CSS-units drift can never reopen the gap.
//
// Props:
//   segments  — array of { key, label, testid?, badge? }; rendered left→right
//               badge: optional ReactNode rendered inline after the label
//   value     — currently-active key
//   onChange  — called with the clicked key
//   testId    — data-testid for the outer pill div (optional)
//   compact   — boolean; reduces button padding and font size for secondary
//               modifier controls (default false — standard size)

import React from "react";
import { T } from "../../app/tokens.js";

export function ToolbarViewToggle({ segments, value, onChange, testId, title, compact = false, ariaLabel = "View mode", disabled = false }) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      title={title}
      data-testid={testId}
      style={{
        display:    "inline-flex",
        background: "var(--v2-hair)",
        borderRadius: compact ? 8 : 10,
        padding:    compact ? 2 : 3,
        gap:        2,
        flexShrink: 0,
      }}
    >
      {segments.map(({ key, label, testid, badge }) => {
        const isActive = value === key;
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            data-testid={testid || `toggle-${key}`}
            onClick={() => onChange(key)}
            aria-pressed={isActive}
            style={{
              border:     "none",
              height:     compact ? 22 : T.toolbarSegH,
              padding:    compact ? "0 9px" : "0 12px",
              borderRadius: compact ? 6 : 8,
              fontSize:   compact ? T.fsXs : T.fsSm5,
              fontWeight: T.fwBold,
              cursor:     "pointer",
              whiteSpace: "nowrap",
              display:    "inline-flex",
              alignItems: "center",
              gap:        6,
              background: isActive ? T.white : "transparent",
              color:      isActive ? T.dark : T.mid,
              boxShadow:  isActive ? T.shadowSegment : "none",
              transition: "all .15s",
            }}
          >
            {label}
            {badge != null && badge !== false && (
              <span
                style={{
                  display:        "inline-flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  borderRadius:   8,
                  padding:        "2px 6px",
                  minWidth:       18,
                  fontSize:       T.fsXxs,
                  fontWeight:     T.fwSemibold,
                  lineHeight:     1,
                  textAlign:      "center",
                  background:     T.utilAmber,
                  color:          T.amberDark,
                  whiteSpace:     "nowrap",
                }}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
